/**
 * Payment Success Page - Trade Loop v2.0
 *
 * Displayed after successful Stripe checkout
 * - Confirms successful payment
 * - Shows subscription details
 * - Redirects to subscription page
 */

import { useEffect, useState } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import AppLayout from '@/components/layout/AppLayout';
import { Card, CardContent } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { CheckCircle, Sparkles, ArrowRight, Loader2, Smartphone } from 'lucide-react';

export default function PaymentSuccessPage() {
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();
  const [isVerifying, setIsVerifying] = useState(true);
  const [error, setError] = useState<string | null>(null);

  const sessionId = searchParams.get('session_id');

  // Set by the hand-off redeem endpoint when checkout started in the native app. A browser that
  // got here from the app is a dead end otherwise: nothing tells the user the job is done, and
  // this browser may be signed in as a DIFFERENT account than the one that just subscribed —
  // which happened in testing and reads as "it upgraded the wrong person". It did not; the
  // hand-off token picks the account, never the browser session.
  const cameFromApp = searchParams.get('fromApp') === '1';

  useEffect(() => {
    // Verify the checkout session
    const verifySession = async () => {
      if (!sessionId) {
        setError('No session ID found');
        setIsVerifying(false);
        return;
      }

      try {
        // TODO: Call API to verify session and get subscription details
        // const response = await fetch(`/api/workflows/subscriptions/verify-session?session_id=${sessionId}`);
        // const data = await response.json();

        // Mock success for now
        await new Promise((resolve) => setTimeout(resolve, 1500));

        setIsVerifying(false);
      } catch (err) {
        console.error('Session verification failed:', err);
        setError('Failed to verify payment');
        setIsVerifying(false);
      }
    };

    verifySession();
  }, [sessionId]);

  const handleContinue = () => {
    navigate('/subscription');
  };

  const handleGoHome = () => {
    navigate('/dashboard');
  };

  if (isVerifying) {
    return (
      <AppLayout activeTab="profile" title="Processing Payment">
        <div className="max-w-2xl mx-auto p-4">
          <Card>
            <CardContent className="p-12 text-center">
              <Loader2 className="w-16 h-16 text-blue-500 mx-auto mb-6 animate-spin" />
              <h2 className="text-2xl font-bold text-gray-900 mb-3">Processing Your Payment</h2>
              <p className="text-gray-600 mb-2">Please wait while we confirm your subscription...</p>
              <p className="text-sm text-gray-500">This should only take a moment.</p>
            </CardContent>
          </Card>
        </div>
      </AppLayout>
    );
  }

  if (error) {
    return (
      <AppLayout activeTab="profile" title="Payment Error">
        <div className="max-w-2xl mx-auto p-4">
          <Card className="border-red-200">
            <CardContent className="p-12 text-center">
              <div className="w-16 h-16 bg-red-100 rounded-full flex items-center justify-center mx-auto mb-6">
                <CheckCircle className="w-8 h-8 text-red-600" />
              </div>
              <h2 className="text-2xl font-bold text-gray-900 mb-3">Verification Failed</h2>
              <p className="text-gray-600 mb-6">{error}</p>
              <div className="flex gap-3 justify-center">
                <Button variant="outline" onClick={() => navigate('/plans')}>
                  Return to Plans
                </Button>
                <Button onClick={handleGoHome}>Go to Dashboard</Button>
              </div>
            </CardContent>
          </Card>
        </div>
      </AppLayout>
    );
  }

  // Standalone, deliberately NOT wrapped in AppLayout: the visitor may not be signed in here at
  // all, and app chrome invites them to carry on browsing when what they want is to go back.
  if (cameFromApp) {
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center p-4">
        <Card className="max-w-md w-full border-green-200 bg-gradient-to-br from-green-50 to-emerald-50">
          <CardContent className="p-8 text-center">
            <div className="w-20 h-20 bg-gradient-to-br from-green-500 to-emerald-600 rounded-full flex items-center justify-center shadow-lg mx-auto mb-6">
              <CheckCircle className="w-10 h-10 text-white" />
            </div>

            <h1 className="text-2xl font-bold text-gray-900 mb-3">You're all set!</h1>
            <p className="text-gray-700 mb-6">
              Your subscription is active. It has been applied to the Trade Loop account you
              started from in the app.
            </p>

            <div className="bg-white rounded-lg border border-gray-200 p-5 mb-6">
              <div className="w-12 h-12 bg-blue-100 rounded-full flex items-center justify-center mx-auto mb-3">
                <Smartphone className="w-6 h-6 text-blue-600" />
              </div>
              <p className="font-semibold text-gray-900 mb-1">Close this tab and reopen Trade Loop</p>
              <p className="text-sm text-gray-600">
                Your new features unlock as soon as the app comes back to the front.
              </p>
            </div>

            <p className="text-xs text-gray-500">
              Signed in here as someone else? That's expected — this browser and the app keep
              separate sessions, and your purchase went to the account you bought it from.
            </p>
          </CardContent>
        </Card>
      </div>
    );
  }

  return (
    <AppLayout activeTab="profile" title="Payment Successful">
      <div className="max-w-2xl mx-auto p-4">
        <Card className="border-green-200 bg-gradient-to-br from-green-50 to-emerald-50">
          <CardContent className="p-12 text-center">
            {/* Success Icon */}
            <div className="relative inline-block mb-6">
              <div className="w-20 h-20 bg-gradient-to-br from-green-500 to-emerald-600 rounded-full flex items-center justify-center shadow-lg">
                <CheckCircle className="w-10 h-10 text-white" />
              </div>
              <div className="absolute -top-2 -right-2">
                <div className="w-8 h-8 bg-yellow-400 rounded-full flex items-center justify-center animate-bounce">
                  <Sparkles className="w-5 h-5 text-yellow-700" />
                </div>
              </div>
            </div>

            {/* Success Message */}
            <h1 className="text-3xl font-bold text-gray-900 mb-3">Payment Successful!</h1>
            <p className="text-lg text-gray-700 mb-2">
              Welcome to your upgraded Trade Loop experience
            </p>
            <p className="text-gray-600 mb-8">
              Your subscription has been activated and you now have access to all premium features.
            </p>

            {/* Features Unlocked */}
            <div className="bg-white rounded-lg p-6 mb-8 text-left max-w-md mx-auto">
              <h3 className="font-semibold text-gray-900 mb-4 text-center">What's Next?</h3>
              <ul className="space-y-3">
                <li className="flex items-start gap-3">
                  <div className="w-5 h-5 bg-green-100 rounded-full flex items-center justify-center flex-shrink-0 mt-0.5">
                    <CheckCircle className="w-3 h-3 text-green-600" />
                  </div>
                  <div>
                    <p className="font-medium text-gray-900">Increased Limits</p>
                    <p className="text-sm text-gray-600">Start using your expanded referral and connection limits</p>
                  </div>
                </li>
                <li className="flex items-start gap-3">
                  <div className="w-5 h-5 bg-green-100 rounded-full flex items-center justify-center flex-shrink-0 mt-0.5">
                    <CheckCircle className="w-3 h-3 text-green-600" />
                  </div>
                  <div>
                    <p className="font-medium text-gray-900">Priority Support</p>
                    <p className="text-sm text-gray-600">Get help faster with our dedicated support team</p>
                  </div>
                </li>
              </ul>
            </div>

            {/* Actions */}
            <div className="flex flex-col sm:flex-row gap-3 justify-center">
              <Button
                onClick={handleContinue}
                className="bg-gradient-to-r from-green-500 to-emerald-600 hover:from-green-600 hover:to-emerald-700 text-white"
              >
                View Subscription Details
                <ArrowRight className="w-4 h-4 ml-2" />
              </Button>
              <Button variant="outline" onClick={handleGoHome}>
                Go to Dashboard
              </Button>
            </div>

            {/* Confirmation Email Notice */}
            <p className="text-sm text-gray-500 mt-6">
              A confirmation email with your receipt has been sent to your email address.
            </p>
          </CardContent>
        </Card>
      </div>
    </AppLayout>
  );
}
